/**
 * ConfigurationPanel — aircraft/vehicle configurations, reference quantities,
 * and design conditions in a three-tab docking panel.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Dialog,
  Flex,
  Heading,
  ScrollArea,
  Select,
  Tabs,
  Text,
  TextArea,
  TextField,
} from "@radix-ui/themes";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, CellValueChangedEvent, GridReadyEvent, RowDataUpdatedEvent } from "ag-grid-community";
import { themeQuartz, colorSchemeDark } from "ag-grid-community";

import { useTheme } from "../../context/ThemeContext";
import { useProvenanceContext } from "../../context/ProvenanceContext";
import { useBranchGuard } from "../../context/BranchGuardContext";
import {
  useConfigurations,
  useCreateConfiguration,
  useDeleteConfiguration,
} from "../../hooks/useConfigurations";
import {
  useReferenceQuantities,
  useUpsertReferenceQuantity,
  useImportReferenceQuantities,
  useDeleteReferenceQuantity,
  useApproveParameter,
  useRejectParameter,
  useReferenceImpact,
} from "../../hooks/useReferenceQuantities";
import type { ReferenceImpactPreview, ReferenceQuantity } from "../../api/referenceQuantities";
import { useCurrentRole, useCurrentUserId } from "../../hooks/useCurrentRole";
import {
  useDesignConditions,
  useUpsertDesignCondition,
  useImportDesignConditions,
  useDeleteDesignCondition,
  useApproveDesignCondition,
  useRejectDesignCondition,
} from "../../hooks/useDesignConditions";
import { useMembers } from "../../hooks/useMembers";

interface Props {
  projectId: string;
}

const AIRCRAFT_TYPES = [
  "fixed_wing",
  "rotorcraft",
  "eVTOL",
  "spacecraft",
  "ground_vehicle",
  "other",
];



function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return "—";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function formatImpactKinds(impact: ReferenceImpactPreview): string {
  return Object.entries(impact.affected_by_kind ?? {})
    .filter(([, count]) => count > 0)
    .map(([kind, count]) => `${count} ${kind.replace(/_/g, " ")}`)
    .join(" · ");
}

function ReferenceImpactCallout({
  impact,
  loading,
  fallbackCount,
}: {
  impact?: ReferenceImpactPreview;
  loading: boolean;
  fallbackCount?: number;
}) {
  const affected = impact?.affected_nodes ?? fallbackCount ?? 0;
  const color = affected > 0 ? "orange" : "green";
  const textColor = affected > 0 ? "var(--orange-11)" : "var(--green-11)";
  const kinds = impact ? formatImpactKinds(impact) : "";

  return (
    <Box
      p="2"
      mt="3"
      style={{
        border: `1px solid var(--${color}-a6)`,
        borderRadius: 8,
        background: `var(--${color}-a2)`,
      }}
    >
      <Text size="2" weight="medium" style={{ color: textColor }}>
        {loading
          ? "Checking downstream impact..."
          : `${affected} downstream ${affected === 1 ? "item" : "items"} affected`}
      </Text>
      {!loading && kinds && (
        <Text as="div" size="1" color="gray" mt="1">
          {kinds}
        </Text>
      )}
      {!loading && impact?.delta_pct != null && (
        <Text as="div" size="1" color="gray" mt="1">
          Δ {impact.delta_pct > 0 ? "+" : ""}{impact.delta_pct}% from current value
        </Text>
      )}
    </Box>
  );
}

interface RefQuantitiesSectionProps {
  refQuantities: ReferenceQuantity[];
  showAddQuantity: boolean;
  setShowAddQuantity: (v: boolean) => void;
  newParam: string;
  setNewParam: (v: string) => void;
  newValue: string;
  setNewValue: (v: string) => void;
  newUnits: string;
  setNewUnits: (v: string) => void;
  newOwners: string[];
  setNewOwners: React.Dispatch<React.SetStateAction<string[]>>;
  newReason: string;
  setNewReason: (v: string) => void;
  memberOptions: { id: string; label: string }[];
  projectId: string;
  currentBranch: string;
  activeConfig: string | null;
  upsertRef: ReturnType<typeof useUpsertReferenceQuantity>;
  refGridRef: React.RefObject<AgGridReact | null>;
  gridTheme: ReturnType<typeof themeQuartz.withPart> | typeof themeQuartz;
  refColumns: ColDef[];
  onRefCellChanged: (event: CellValueChangedEvent) => void;
  autoSizeColumns: (event: GridReadyEvent | RowDataUpdatedEvent) => void;
  handleCsvImport: (target: "ref" | "condition", event: React.ChangeEvent<HTMLInputElement>) => void;
  expandGroup: string | null;
}

function RefQuantitiesSection({
  refQuantities,
  showAddQuantity,
  setShowAddQuantity,
  newParam,
  setNewParam,
  newValue,
  setNewValue,
  newUnits,
  setNewUnits,
  newOwners,
  setNewOwners,
  newReason,
  setNewReason,
  memberOptions,
  projectId,
  currentBranch,
  activeConfig,
  upsertRef,
  refGridRef,
  gridTheme,
  refColumns,
  onRefCellChanged,
  autoSizeColumns,
  handleCsvImport,
  expandGroup,
}: RefQuantitiesSectionProps) {
  const pendingCount = refQuantities.filter((q) => q.approval_status === "pending_review").length;
  const staleCount = refQuantities.filter((q) => q.is_stale).length;

  const handleGridReady = useCallback((event: GridReadyEvent) => {
    autoSizeColumns(event);
    if (expandGroup) {
      event.api.forEachNode((node) => {
        if (node.group && node.key === expandGroup) node.setExpanded(true);
      });
    }
  }, [autoSizeColumns, expandGroup]);

  const handleRowDataUpdated = useCallback((event: RowDataUpdatedEvent) => {
    autoSizeColumns(event);
    if (expandGroup) {
      event.api.forEachNode((node) => {
        if (node.group && node.key === expandGroup) node.setExpanded(true);
      });
    }
  }, [autoSizeColumns, expandGroup]);

  return (
    <Box>
      <Flex align="center" gap="2" mb="2" style={{ padding: "4px 0" }}>
        <Text size="2" weight="medium">Reference Quantities</Text>
        <Flex gap="2" align="center">
          <Text size="1" color="gray">{refQuantities.length} {refQuantities.length === 1 ? "quantity" : "quantities"}</Text>
          {pendingCount > 0 && <Badge size="1" color="orange">{pendingCount} pending</Badge>}
          {staleCount > 0 && <Badge size="1" color="amber">{staleCount} stale</Badge>}
        </Flex>
      </Flex>

      <Box
        style={{
          border: "1px solid var(--gray-a4)",
          borderRadius: 6,
        }}
      >
        <Flex gap="2" p="2" style={{ borderBottom: "1px solid var(--gray-a3)" }}>
          <Button size="1" variant="soft" onClick={() => setShowAddQuantity(true)}>
            Add Quantity
          </Button>
          <Dialog.Root open={showAddQuantity} onOpenChange={setShowAddQuantity}>
            <Dialog.Content maxWidth="420px">
              <Dialog.Title>Add Reference Quantity</Dialog.Title>
              <Flex direction="column" gap="3" mt="3">
                <TextField.Root
                  placeholder="Parameter name (e.g. wing_area, ref_chord, wing_span)"
                  value={newParam}
                  onChange={(e) => setNewParam(e.target.value)}
                />
                <Flex gap="2">
                  <TextField.Root
                    placeholder="Value"
                    type="number"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <TextField.Root
                    placeholder="Units"
                    value={newUnits}
                    onChange={(e) => setNewUnits(e.target.value)}
                    style={{ width: 100 }}
                  />
                </Flex>
                <Box>
                  <Text size="2" weight="medium" mb="1">Owners</Text>
                  <Flex direction="column" gap="1" mt="1">
                    {memberOptions.map((m) => (
                      <label key={m.id} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                        <Checkbox
                          checked={newOwners.includes(m.id)}
                          onCheckedChange={(checked) => {
                            setNewOwners((prev) =>
                              checked ? [...prev, m.id] : prev.filter((id) => id !== m.id),
                            );
                          }}
                        />
                        <Text size="2">{m.label}</Text>
                      </label>
                    ))}
                    {memberOptions.length === 0 && (
                      <Text size="1" color="gray">No project members found.</Text>
                    )}
                  </Flex>
                </Box>
                <TextField.Root
                  placeholder="Reason (required)"
                  value={newReason}
                  onChange={(e) => setNewReason(e.target.value)}
                  color={newReason.trim() ? undefined : "red"}
                />
                <Flex gap="2" justify="end">
                  <Dialog.Close>
                    <Button variant="soft" color="gray">Cancel</Button>
                  </Dialog.Close>
                  <Button
                    disabled={!newParam.trim() || !newReason.trim()}
                    onClick={() => {
                      upsertRef.mutate(
                        {
                          project_id: projectId,
                          branch: currentBranch,
                          configuration_id: activeConfig ?? undefined,
                          parameter: newParam.trim(),
                          value: parseFloat(newValue) || 0,
                          units: newUnits || undefined,
                          owned_by: newOwners.length ? newOwners.join(",") : undefined,
                          change_reason: newReason || undefined,
                        },
                        {
                          onSuccess: () => {
                            setNewParam("");
                            setNewValue("0");
                            setNewUnits("");
                            setNewOwners([]);
                            setNewReason("");
                            setShowAddQuantity(false);
                          },
                        },
                      );
                    }}
                  >
                    Add
                  </Button>
                </Flex>
              </Flex>
            </Dialog.Content>
          </Dialog.Root>
          <label>
            <Button size="1" variant="soft" asChild>
              <span>Import CSV</span>
            </Button>
            <input
              type="file"
              accept=".csv"
              style={{ display: "none" }}
              onChange={(e) => handleCsvImport("ref", e)}
            />
          </label>
        </Flex>
        <div style={{ maxHeight: 320, overflowY: "auto", width: "100%" }}>
          <AgGridReact
            ref={refGridRef}
            theme={gridTheme}
            rowData={refQuantities}
            columnDefs={refColumns}
            onCellValueChanged={onRefCellChanged}
            onGridReady={handleGridReady}
            onRowDataUpdated={handleRowDataUpdated}
            getRowStyle={(params) =>
              params.data?.is_stale
                ? { borderLeft: "3px solid var(--amber-9)" }
                : undefined
            }
            domLayout="autoHeight"
            suppressMovableColumns
          />
        </div>
      </Box>
    </Box>
  );
}

type ConfigTab = "configurations" | "reference-quantities" | "design-conditions";
const VALID_CONFIG_TABS = new Set<string>(["configurations", "reference-quantities", "design-conditions"]);

export default function ConfigurationPanel({ projectId }: Props) {
  const { theme } = useTheme();
  const gridTheme = theme === "dark" ? themeQuartz.withPart(colorSchemeDark) : themeQuartz;
  const { currentBranch } = useProvenanceContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const configTabParam = searchParams.get("configTab");
  const initialTab: ConfigTab = configTabParam && VALID_CONFIG_TABS.has(configTabParam)
    ? (configTabParam as ConfigTab)
    : "configurations";
  const [activeConfigTab, setActiveConfigTab] = useState<ConfigTab>(initialTab);
  const refGroupParam = searchParams.get("refGroup");

  useEffect(() => {
    if (configTabParam && VALID_CONFIG_TABS.has(configTabParam)) {
      setActiveConfigTab(configTabParam as ConfigTab);
    }
  }, [configTabParam]);

  const { guard } = useBranchGuard();
  const [activeConfig, setActiveConfig] = useState<string | null>(null);
  const [showAddConfig, setShowAddConfig] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showAddQuantity, setShowAddQuantity] = useState(false);
  const [showAddCondition, setShowAddCondition] = useState(false);
  const [pendingEdit, setPendingEdit] = useState<{ event: CellValueChangedEvent; table: "ref" | "cond" } | null>(null);
  const [editReason, setEditReason] = useState("");
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string; table: "ref" | "cond" } | null>(null);
  const [deleteReason, setDeleteReason] = useState("");
  const refGridRef = useRef<AgGridReact>(null);
  const condGridRef = useRef<AgGridReact>(null);

  // -- Add Quantity form state --
  const [newParam, setNewParam] = useState("");
  const [newValue, setNewValue] = useState("0");
  const [newUnits, setNewUnits] = useState("");
  const [newOwners, setNewOwners] = useState<string[]>([]);
  const [newReason, setNewReason] = useState("");

  // -- Add Condition form state --
  const [newCondParam, setNewCondParam] = useState("");
  const [newCondValue, setNewCondValue] = useState("0");
  const [newCondUnits, setNewCondUnits] = useState("");
  const [newCondCategory, setNewCondCategory] = useState("__none__");
  const [newCondDesc, setNewCondDesc] = useState("");

  // -- Configuration form state --
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState("fixed_wing");
  const [newDesc, setNewDesc] = useState("");

  // -- Queries --
  const { data: configurations = [] } = useConfigurations(projectId);
  const createConfig = useCreateConfiguration(projectId);
  const deleteConfig = useDeleteConfiguration(projectId);

  const { data: refQuantities = [] } = useReferenceQuantities(
    projectId,
    currentBranch,
    activeConfig ?? undefined,
  );
  const upsertRef = useUpsertReferenceQuantity(projectId, currentBranch);
  const importRefs = useImportReferenceQuantities(projectId, currentBranch);
  const pendingRefImpactRequest = useMemo(() => {
    if (!pendingEdit || pendingEdit.table !== "ref") return null;
    const row = pendingEdit.event.data as ReferenceQuantity;
    const nextValue = Number(row.value);
    if (!Number.isFinite(nextValue)) return null;
    return {
      parameter: row.parameter,
      path: row.path,
      value: nextValue,
      downstreamCount: row.downstream_count,
    };
  }, [pendingEdit]);
  const { data: pendingRefImpact, isFetching: pendingRefImpactLoading } = useReferenceImpact(
    projectId,
    currentBranch,
    pendingRefImpactRequest?.parameter ?? "",
    pendingRefImpactRequest?.value ?? 0,
    !!pendingRefImpactRequest,
    pendingRefImpactRequest?.path,
  );

  const { data: designConditions = [] } = useDesignConditions(activeConfig);
  const upsertCondition = useUpsertDesignCondition(activeConfig);
  const importConditions = useImportDesignConditions(activeConfig);
  const deleteRef = useDeleteReferenceQuantity(projectId, currentBranch);
  const deleteCondition = useDeleteDesignCondition(activeConfig);
  const approveCond = useApproveDesignCondition(activeConfig);
  const rejectCond = useRejectDesignCondition(activeConfig);

  const currentUserId = useCurrentUserId() ?? "";
  const { isOwner: isProjectOwner } = useCurrentRole(projectId);
  const approveParam = useApproveParameter(projectId, currentBranch);
  const rejectParam = useRejectParameter(projectId, currentBranch);

  const { data: members = [] } = useMembers(projectId);
  const memberOptions = useMemo(
    () => members.map((m) => ({ id: m.user_id, label: m.display_name || m.email || m.user_id })),
    [members],
  );

  const autoSizeColumns = useCallback((event: GridReadyEvent | RowDataUpdatedEvent) => {
    event.api.autoSizeAllColumns();
  }, []);

  // -- Configuration CRUD --
  function handleAddConfiguration() {
    if (!newName.trim()) return;
    createConfig.mutate(
      {
        project_id: projectId,
        name: newName.trim(),
        aircraft_type: newType,
        description: newDesc || undefined,
      },
      {
        onSuccess: () => {
          setNewName("");
          setNewDesc("");
          setShowAddConfig(false);
        },
      },
    );
  }

  // -- CSV Import helper --
  function handleCsvImport(
    target: "ref" | "condition",
    event: React.ChangeEvent<HTMLInputElement>,
  ) {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.trim().split("\n");
      const header = lines[0];
      if (!header) return;
      const headers = header.split(",").map((h) => h.trim().toLowerCase());
      const rows = lines.slice(1).map((line) => {
        const values = line.split(",").map((v) => v.trim());
        const obj: Record<string, string> = {};
        headers.forEach((h, i) => (obj[h] = values[i] ?? ""));
        return obj;
      });

      if (target === "ref") {
        const quantities = rows
          .filter((r) => r["parameter"])
          .map((r) => ({
            parameter: r["parameter"]!,
            value: parseFloat(r["value"] ?? "0"),
            units: r["units"] || undefined,
            owned_by: r["owned_by"] || undefined,
          }));
        importRefs.mutate({
          quantities,
          configurationId: activeConfig ?? undefined,
        });
      } else if (activeConfig) {
        const conditions = rows
          .filter((r) => r["parameter"])
          .map((r) => ({
            parameter: r["parameter"]!,
            value: parseFloat(r["value"] ?? "0"),
            units: r["units"] || undefined,
            category: r["category"] || undefined,
          }));
        importConditions.mutate({ projectId, conditions });
      }
    };
    reader.readAsText(file);
    event.target.value = "";
  }

  // -- AG Grid: Reference Quantities --
  const PARAM_DISPLAY: Record<string, string> = {
    wing_area: "Wing Area",
    ref_chord: "Reference Chord (MAC)",
    wing_span: "Wing Span",
  };
  const refColumns: ColDef[] = useMemo(
    () => [
      {
        field: "geometry_label",
        headerName: "Geometry",
        width: 130,
        rowGroup: true,
        hide: true,
        valueGetter: (params: { data: { path?: string } }) => {
          const parts = (params.data?.path ?? "").split(".");
          return parts.length >= 3 ? parts[1] : "—";
        },
      },
      {
        field: "parameter",
        headerName: "Parameter",
        valueFormatter: (params: { value: string }) =>
          PARAM_DISPLAY[params.value] ?? params.value,
      },
      {
        field: "value",
        headerName: "Value",
        editable: (params) => params.data?.approval_status !== "pending_review",
        type: "numericColumn",
      },
      {
        field: "units",
        headerName: "Units",
        editable: (params) => params.data?.approval_status !== "pending_review",
      },
      {
        field: "version",
        headerName: "Version",
        width: 90,
        type: "numericColumn",
      },
      {
        field: "source",
        headerName: "Source",
        width: 140,
        valueFormatter: (params: { value: string }) => {
          if (!params.value) return "✎ manual";
          if (params.value === "flow360_default" || params.value === "flow360") return "◆ Flow360";
          if (params.value === "flow360-import") return "◆ Flow360 import";
          if (params.value === "wing_area_detection") return "◆ mesh detected";
          if (params.value === "mesh_analysis") return "◆ mesh analysis";
          if (params.value === "user_input") return "✎ manual";
          return params.value;
        },
      },
      {
        field: "owned_by",
        headerName: "Owners",
        editable: true,
        valueFormatter: (params: { value: string }) => {
          if (!params.value) return "";
          return params.value
            .split(",")
            .map((id: string) => memberOptions.find((m) => m.id === id.trim())?.label ?? id.trim())
            .join(", ");
        },
      },
      {
        field: "approval_status",
        headerName: "Status",
        cellStyle: (params: { value: string }) => {
          const colorMap: Record<string, string> = {
            approved: "var(--green-11)",
            pending_review: "var(--orange-11)",
            draft: "var(--gray-11)",
            rejected: "var(--red-11)",
          };
          return { color: colorMap[params.value] ?? "var(--gray-11)", fontWeight: 500 };
        },
      },
      {
        field: "updated_at",
        headerName: "Updated",
        valueFormatter: (params: { value: string }) =>
          formatRelativeTime(params.value),
      },
      {
        field: "downstream_count",
        headerName: "Stale Deps",
        type: "numericColumn",
        cellStyle: (params: { value: number }) =>
          params.value > 0
            ? { color: "var(--red-11)", fontWeight: 600 }
            : undefined,
      },
      { field: "change_reason", headerName: "Last Reason" },
      {
        headerName: "Actions",
        width: 180,
        suppressSizeToFit: true,
        cellRenderer: (params: { data?: { id: string; parameter: string; approval_status: string; owned_by: string | null } }) => {
          const d = params.data;
          if (!d) return null;
          const isPending = d.approval_status === "pending_review";
          const canApprove = isPending && (
            (d.owned_by && d.owned_by.split(",").includes(currentUserId)) || isProjectOwner
          );
          return (
            <span style={{ display: "flex", gap: 4, alignItems: "center" }}>
              {canApprove && (
                <>
                  <button
                    style={{ color: "var(--green-11)", background: "none", border: "none", cursor: "pointer", fontSize: 12, padding: "2px 6px" }}
                    onClick={() => approveParam.mutate({ nodeId: d.id })}
                  >
                    Approve
                  </button>
                  <button
                    style={{ color: "var(--red-11)", background: "none", border: "none", cursor: "pointer", fontSize: 12, padding: "2px 6px" }}
                    onClick={() => rejectParam.mutate({ nodeId: d.id })}
                  >
                    Reject
                  </button>
                </>
              )}
              {isPending && !canApprove && (
                <span style={{ color: "var(--orange-11)", fontSize: 11 }}>Awaiting owner</span>
              )}
              <button
                style={{ color: "var(--red-11)", background: "none", border: "none", cursor: "pointer", fontSize: 12, padding: "2px 6px" }}
                onClick={() => setPendingDelete({ id: d.id, name: d.parameter, table: "ref" })}
              >
                Delete
              </button>
            </span>
          );
        },
      },
    ],
    [memberOptions, currentUserId, isProjectOwner, approveParam, rejectParam],
  );

  const onRefCellChanged = useCallback(
    (event: CellValueChangedEvent) => {
      setPendingEdit({ event, table: "ref" });
      setEditReason("");
    },
    [],
  );

  function commitPendingEdit() {
    if (!pendingEdit || !editReason.trim()) return;
    const row = pendingEdit.event.data;
    const table = pendingEdit.table;
    const reason = editReason.trim();
    const hint = table === "ref" ? row.parameter : `condition-${row.parameter}`;

    guard(() => {
      if (table === "ref") {
        upsertRef.mutate({
          project_id: projectId,
          branch: currentBranch,
          configuration_id: activeConfig ?? undefined,
          parameter: row.parameter,
          value: parseFloat(row.value),
          units: row.units ?? undefined,
          owned_by: row.owned_by ?? undefined,
          change_reason: reason,
        });
      } else {
        if (!activeConfig) return;
        upsertCondition.mutate({
          configuration_id: activeConfig,
          project_id: projectId,
          parameter: row.parameter,
          value: parseFloat(row.value),
          units: row.units ?? undefined,
          category: row.category ?? undefined,
          description: row.description ?? undefined,
          owned_by: row.owned_by ?? undefined,
          change_reason: reason,
        });
      }
    }, { hint });

    setPendingEdit(null);
    setEditReason("");
  }

  function cancelPendingEdit() {
    if (pendingEdit) {
      const { event } = pendingEdit;
      event.node.setDataValue(event.column.getColId(), event.oldValue);
    }
    setPendingEdit(null);
    setEditReason("");
  }

  function commitPendingDelete() {
    if (!pendingDelete || !deleteReason.trim()) return;
    const { id, name: paramName, table } = pendingDelete;
    const reason = deleteReason.trim();

    guard(() => {
      if (table === "ref") {
        deleteRef.mutate({ nodeId: id, reason });
      } else {
        deleteCondition.mutate({ id, reason });
      }
    }, { hint: `delete-${paramName}` });

    setPendingDelete(null);
    setDeleteReason("");
  }

  // -- AG Grid: Design Conditions --
  const filteredConditions = useMemo(() => {
    if (categoryFilter === "all") return designConditions;
    return designConditions.filter((c) => c.category === categoryFilter);
  }, [designConditions, categoryFilter]);

  const conditionColumns: ColDef[] = useMemo(
    () => [
      { field: "parameter", headerName: "Parameter" },
      {
        field: "value",
        headerName: "Value",
        editable: (params) => params.data?.approval_status !== "pending_review",
        type: "numericColumn",
      },
      {
        field: "units",
        headerName: "Units",
        editable: (params) => params.data?.approval_status !== "pending_review",
      },
      {
        field: "category",
        headerName: "Category",
        editable: true,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: ["", "speed", "weight", "altitude", "structural"] },
      },
      {
        field: "owned_by",
        headerName: "Owner",
        editable: true,
        valueFormatter: (params: { value: string }) => {
          if (!params.value) return "";
          return params.value
            .split(",")
            .map((id: string) => memberOptions.find((m) => m.id === id.trim())?.label ?? id.trim())
            .join(", ");
        },
      },
      {
        field: "approval_status",
        headerName: "Status",
        cellStyle: (params: { value: string }) => {
          const colorMap: Record<string, string> = {
            approved: "var(--green-11)",
            pending_review: "var(--orange-11)",
            draft: "var(--gray-11)",
            rejected: "var(--red-11)",
          };
          return { color: colorMap[params.value] ?? "var(--gray-11)", fontWeight: 500 };
        },
      },
      { field: "description", headerName: "Description", editable: true },
      { field: "change_reason", headerName: "Last Reason" },
      {
        headerName: "Actions",
        width: 180,
        suppressSizeToFit: true,
        cellRenderer: (params: { data?: { id: string; parameter: string; approval_status: string; owned_by: string | null } }) => {
          const d = params.data;
          if (!d) return null;
          const isPending = d.approval_status === "pending_review";
          const canApprove = isPending && (
            (d.owned_by && d.owned_by.split(",").includes(currentUserId)) || isProjectOwner
          );
          return (
            <span style={{ display: "flex", gap: 4, alignItems: "center" }}>
              {canApprove && (
                <>
                  <button
                    style={{ color: "var(--green-11)", background: "none", border: "none", cursor: "pointer", fontSize: 12, padding: "2px 6px" }}
                    onClick={() => approveCond.mutate({ conditionId: d.id })}
                  >
                    Approve
                  </button>
                  <button
                    style={{ color: "var(--red-11)", background: "none", border: "none", cursor: "pointer", fontSize: 12, padding: "2px 6px" }}
                    onClick={() => rejectCond.mutate({ conditionId: d.id })}
                  >
                    Reject
                  </button>
                </>
              )}
              {isPending && !canApprove && (
                <span style={{ color: "var(--orange-11)", fontSize: 11 }}>Awaiting owner</span>
              )}
              <button
                style={{ color: "var(--red-11)", background: "none", border: "none", cursor: "pointer", fontSize: 12, padding: "2px 6px" }}
                onClick={() => setPendingDelete({ id: d.id, name: d.parameter, table: "cond" })}
              >
                Delete
              </button>
            </span>
          );
        },
      },
    ],
    [memberOptions, currentUserId, isProjectOwner, approveCond, rejectCond],
  );

  const onConditionCellChanged = useCallback(
    (event: CellValueChangedEvent) => {
      setPendingEdit({ event, table: "cond" });
      setEditReason("");
    },
    [],
  );

  return (
    <Box p="3" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Heading size="3" mb="3">
        Aircraft &amp; References
      </Heading>

      <Tabs.Root
        value={activeConfigTab}
        onValueChange={(v) => {
          setActiveConfigTab(v as ConfigTab);
          setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete("configTab"); return next; }, { replace: true });
        }}
        style={{ flex: 1, display: "flex", flexDirection: "column" }}
      >
        <Tabs.List>
          <Tabs.Trigger value="configurations">Configurations</Tabs.Trigger>
          <Tabs.Trigger value="reference-quantities">Reference Quantities</Tabs.Trigger>
          <Tabs.Trigger value="design-conditions">Design Conditions</Tabs.Trigger>
        </Tabs.List>

        {/* Tab 1: Configurations */}
        <Tabs.Content value="configurations" style={{ flex: 1 }}>
          <ScrollArea style={{ height: "100%" }}>
            <Flex direction="column" gap="2" p="2">
              {configurations.length === 0 && !showAddConfig && (
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  gap="2"
                  py="6"
                >
                  <Text size="2" color="gray">
                    No configurations yet. Create one to organize reference
                    quantities by aircraft variant.
                  </Text>
                  <Button size="2" onClick={() => setShowAddConfig(true)}>
                    Add Configuration
                  </Button>
                </Flex>
              )}

              {configurations.map((cfg) => (
                <Box
                  key={cfg.id}
                  p="3"
                  onClick={() => setActiveConfig(cfg.id)}
                  style={{
                    border:
                      activeConfig === cfg.id
                        ? "2px solid var(--accent-9)"
                        : "1px solid var(--gray-a4)",
                    borderRadius: "var(--radius-2)",
                    cursor: "pointer",
                  }}
                >
                  <Flex justify="between" align="center">
                    <Flex direction="column" gap="1">
                      <Text weight="medium" size="2">
                        {cfg.display_name || cfg.name}
                      </Text>
                      {cfg.description && (
                        <Text size="1" color="gray" truncate>
                          {cfg.description}
                        </Text>
                      )}
                    </Flex>
                    <Flex gap="2" align="center">
                      {cfg.aircraft_type && (
                        <Badge size="1" color="blue">
                          {cfg.aircraft_type}
                        </Badge>
                      )}
                      <Button
                        size="1"
                        variant="ghost"
                        color="red"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteConfig.mutate(cfg.id);
                          if (activeConfig === cfg.id) setActiveConfig(null);
                        }}
                      >
                        Delete
                      </Button>
                    </Flex>
                  </Flex>
                </Box>
              ))}

              {configurations.length > 0 && !showAddConfig && (
                <Button
                  size="2"
                  variant="soft"
                  onClick={() => setShowAddConfig(true)}
                >
                  Add Configuration
                </Button>
              )}

              {showAddConfig && (
                <Box
                  p="3"
                  style={{
                    border: "1px solid var(--gray-a4)",
                    borderRadius: "var(--radius-2)",
                  }}
                >
                  <Flex direction="column" gap="2">
                    <TextField.Root
                      placeholder="Configuration name"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                    />
                    <Select.Root value={newType} onValueChange={setNewType}>
                      <Select.Trigger placeholder="Aircraft type" />
                      <Select.Content>
                        {AIRCRAFT_TYPES.map((t) => (
                          <Select.Item key={t} value={t}>
                            {t}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Root>
                    <TextArea
                      placeholder="Description (optional)"
                      value={newDesc}
                      onChange={(e) => setNewDesc(e.target.value)}
                      rows={2}
                    />
                    <Flex gap="2" justify="end">
                      <Button
                        variant="soft"
                        color="gray"
                        onClick={() => setShowAddConfig(false)}
                      >
                        Cancel
                      </Button>
                      <Button onClick={handleAddConfiguration}>Create</Button>
                    </Flex>
                  </Flex>
                </Box>
              )}
            </Flex>
          </ScrollArea>
        </Tabs.Content>

        {/* Tab 2: Reference Quantities */}
        <Tabs.Content value="reference-quantities" style={{ flex: 1 }}>
          <ScrollArea style={{ height: "100%" }}>
            <Box p="2">
              <RefQuantitiesSection
                refQuantities={refQuantities}
                showAddQuantity={showAddQuantity}
                setShowAddQuantity={setShowAddQuantity}
                newParam={newParam}
                setNewParam={setNewParam}
                newValue={newValue}
                setNewValue={setNewValue}
                newUnits={newUnits}
                setNewUnits={setNewUnits}
                newOwners={newOwners}
                setNewOwners={setNewOwners}
                newReason={newReason}
                setNewReason={setNewReason}
                memberOptions={memberOptions}
                projectId={projectId}
                currentBranch={currentBranch}
                activeConfig={activeConfig}
                upsertRef={upsertRef}
                refGridRef={refGridRef}
                gridTheme={gridTheme}
                refColumns={refColumns}
                onRefCellChanged={onRefCellChanged}
                autoSizeColumns={autoSizeColumns}
                handleCsvImport={handleCsvImport}
                expandGroup={refGroupParam}
              />
            </Box>
          </ScrollArea>
        </Tabs.Content>

        {/* Tab 3: Design Conditions */}
        <Tabs.Content value="design-conditions" style={{ flex: 1 }}>
          <Flex direction="column" gap="2" style={{ height: "100%" }}>
            <Flex gap="2" p="2" align="center">
              <Select.Root value={categoryFilter} onValueChange={setCategoryFilter}>
                <Select.Trigger placeholder="Category" />
                <Select.Content>
                  <Select.Item value="all">All</Select.Item>
                  <Select.Item value="speed">Speed</Select.Item>
                  <Select.Item value="weight">Weight</Select.Item>
                  <Select.Item value="altitude">Altitude</Select.Item>
                  <Select.Item value="structural">Structural</Select.Item>
                </Select.Content>
              </Select.Root>
              <Button size="1" variant="soft" disabled={!activeConfig} onClick={() => setShowAddCondition(true)}>
                Add Condition
              </Button>
              <Dialog.Root open={showAddCondition} onOpenChange={setShowAddCondition}>
                <Dialog.Content maxWidth="420px">
                  <Dialog.Title>Add Design Condition</Dialog.Title>
                  <Flex direction="column" gap="3" mt="3">
                    <TextField.Root
                      placeholder="Parameter name"
                      value={newCondParam}
                      onChange={(e) => setNewCondParam(e.target.value)}
                    />
                    <Flex gap="2">
                      <TextField.Root
                        placeholder="Value"
                        type="number"
                        value={newCondValue}
                        onChange={(e) => setNewCondValue(e.target.value)}
                        style={{ flex: 1 }}
                      />
                      <TextField.Root
                        placeholder="Units"
                        value={newCondUnits}
                        onChange={(e) => setNewCondUnits(e.target.value)}
                        style={{ width: 100 }}
                      />
                    </Flex>
                    <Select.Root value={newCondCategory} onValueChange={setNewCondCategory}>
                      <Select.Trigger placeholder="Category (optional)" />
                      <Select.Content>
                        <Select.Item value="__none__">None</Select.Item>
                        <Select.Item value="speed">Speed</Select.Item>
                        <Select.Item value="weight">Weight</Select.Item>
                        <Select.Item value="altitude">Altitude</Select.Item>
                        <Select.Item value="structural">Structural</Select.Item>
                      </Select.Content>
                    </Select.Root>
                    <TextField.Root
                      placeholder="Description (optional)"
                      value={newCondDesc}
                      onChange={(e) => setNewCondDesc(e.target.value)}
                    />
                    <Flex gap="2" justify="end">
                      <Dialog.Close>
                        <Button variant="soft" color="gray">Cancel</Button>
                      </Dialog.Close>
                      <Button
                        disabled={!newCondParam.trim() || !activeConfig}
                        onClick={() => {
                          if (!activeConfig) return;
                          upsertCondition.mutate(
                            {
                              configuration_id: activeConfig,
                              project_id: projectId,
                              parameter: newCondParam.trim(),
                              value: parseFloat(newCondValue) || 0,
                              units: newCondUnits || undefined,
                              category: newCondCategory !== "__none__" ? newCondCategory : undefined,
                              description: newCondDesc || undefined,
                            },
                            {
                              onSuccess: () => {
                                setNewCondParam("");
                                setNewCondValue("0");
                                setNewCondUnits("");
                                setNewCondCategory("");
                                setNewCondDesc("");
                                setShowAddCondition(false);
                              },
                            },
                          );
                        }}
                      >
                        Add
                      </Button>
                    </Flex>
                  </Flex>
                </Dialog.Content>
              </Dialog.Root>
              <label>
                <Button size="1" variant="soft" asChild>
                  <span>Import CSV</span>
                </Button>
                <input
                  type="file"
                  accept=".csv"
                  style={{ display: "none" }}
                  onChange={(e) => handleCsvImport("condition", e)}
                />
              </label>
            </Flex>
            {!activeConfig ? (
              <Flex justify="center" py="6">
                <Text size="2" color="gray">
                  Select a configuration to view design conditions.
                </Text>
              </Flex>
            ) : (
              <div style={{ flex: 1, width: "100%" }}>
                <AgGridReact
                  ref={condGridRef}
                  theme={gridTheme}
                  rowData={filteredConditions}
                  columnDefs={conditionColumns}
                  onCellValueChanged={onConditionCellChanged}
                  onGridReady={autoSizeColumns}
                  onRowDataUpdated={autoSizeColumns}
                  domLayout="autoHeight"
                  suppressMovableColumns
                />
              </div>
            )}
          </Flex>
        </Tabs.Content>
      </Tabs.Root>

      {/* Reason dialog for inline edits */}
      <Dialog.Root open={!!pendingEdit} onOpenChange={(open) => { if (!open) cancelPendingEdit(); }}>
        <Dialog.Content maxWidth="380px">
          <Dialog.Title>Reason for Change</Dialog.Title>
          <Text size="2" color="gray" mb="3">
            Explain why this value was changed. This is recorded for audit trail.
          </Text>
          {pendingEdit?.table === "ref" && (
            <ReferenceImpactCallout
              impact={pendingRefImpact}
              loading={pendingRefImpactLoading}
              fallbackCount={pendingRefImpactRequest?.downstreamCount}
            />
          )}
          <TextField.Root
            placeholder="e.g. Updated per flight test data rev 3"
            value={editReason}
            onChange={(e) => setEditReason(e.target.value)}
            autoFocus
            onKeyDown={(e) => { if (e.key === "Enter" && editReason.trim()) commitPendingEdit(); }}
          />
          <Flex gap="2" justify="end" mt="3">
            <Button variant="soft" color="gray" onClick={cancelPendingEdit}>
              Cancel
            </Button>
            <Button disabled={!editReason.trim()} onClick={commitPendingEdit}>
              Save
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* Reason dialog for soft-delete */}
      <Dialog.Root open={!!pendingDelete} onOpenChange={(open) => { if (!open) { setPendingDelete(null); setDeleteReason(""); } }}>
        <Dialog.Content maxWidth="380px">
          <Dialog.Title>Delete &ldquo;{pendingDelete?.name}&rdquo;</Dialog.Title>
          <Text size="2" color="gray" mb="3">
            This parameter will be soft-deleted. Provide a reason for the audit trail.
          </Text>
          <TextField.Root
            placeholder="e.g. Superseded by updated spec rev 4"
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            autoFocus
            onKeyDown={(e) => { if (e.key === "Enter" && deleteReason.trim()) commitPendingDelete(); }}
          />
          <Flex gap="2" justify="end" mt="3">
            <Button variant="soft" color="gray" onClick={() => { setPendingDelete(null); setDeleteReason(""); }}>
              Cancel
            </Button>
            <Button color="red" disabled={!deleteReason.trim()} onClick={commitPendingDelete}>
              Delete
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </Box>
  );
}
